import { FunctionComponent, useMemo, useState } from "react";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import {
  Button,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Backdrop,
  CircularProgress,
  Typography,
} from "@mui/material";

import { useFleets } from "../../hooks/useFleets";
import { ConfirmModal, EditFleetModal, AddFleetModal } from "..";
import useAuthSnapshot from "../../hooks/useAuthSnapshot";

export const DEFAULT_FLEET_NAME = "All aircraft";

export const isDefaultFleet = (fleet: Fleet) => fleet.name === DEFAULT_FLEET_NAME;

export const getDefaultFleet = (fleets: Fleet[] = []) =>
  fleets.find(isDefaultFleet) ?? null;

export const getCustomFleets = (fleets: Fleet[] = []) =>
  fleets.filter((fleet) => !isDefaultFleet(fleet));

export const formatFleetAircraft = (aircraft?: Aircraft[]) => {
  const registrations = (Array.isArray(aircraft) ? aircraft : [])
    .map((item) => item.registration)
    .filter(Boolean);
  return registrations.length ? registrations.join(", ") : "No aircraft";
};

export const FleetsList: FunctionComponent = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const { userData: storedUser, role, operatorRef } = useAuthSnapshot();
  const {
    fleets: { data, isLoading, isSuccess },
    setIsDeleteModalOpen,
    handleOpenDeleteModal,
    isDeleteModalOpen,
    handleDeleteFleet,
    setIsEditModalOpen,
    handleOpenEditModal,
    isEditModalOpen,
    selectedFleet,
  } = useFleets(operatorRef);

  const fleets = Array.isArray(data) ? data : [];
  const defaultFleet = useMemo(() => getDefaultFleet(fleets), [fleets]);
  const customFleets = useMemo(() => getCustomFleets(fleets), [fleets]);
  const allAircraft = defaultFleet?.aircraft || [];

  if (isLoading) {
    return (
      <Backdrop sx={{ zIndex: 1 }} open={isLoading || false}>
        <CircularProgress />
      </Backdrop>
    )
  }

  return isSuccess && fleets.length ? (
    <>
      <Typography
        component="h1"
        variant="h4"
        sx={{ pb: 3, pt: 1 }}
      >
        Manage fleets
      </Typography>
      {!!storedUser?.operator_name && (
        <Typography component="p" variant="subtitle1" sx={{ pb: 2 }}>
          Operator: {storedUser.operator_name} {role === "super_admin" && "(admin view)"}
        </Typography>
      )}
      <Button
        size="large"
        variant="contained"
        sx={{ mr: 2, mb: 5, height: "56px", minWidth: "220px" }}
        onClick={() => setIsAddModalOpen(true)}
      >
        Add Fleet
      </Button>
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table aria-label="Fleets">
          <TableBody>
            {customFleets.map((row: Fleet) => (
              <TableRow
                key={row.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell>
                  <Typography component="span">{row.name}</Typography>
                  <Typography component="p" variant="body2" color="text.secondary">
                    {formatFleetAircraft(row.aircraft)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip placement="top" title="Edit Fleet">
                    <BorderColorIcon fontSize="medium" sx={{ mr: 2, cursor: "pointer" }} onClick={() => handleOpenEditModal(row)} />
                  </Tooltip>
                  <Tooltip placement="top" title="Delete Fleet">
                    <DeleteForeverIcon fontSize="medium" sx={{ mr: 2, cursor: "pointer" }} onClick={() => handleOpenDeleteModal(row)} />
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        text={`Are you sure you want to delete fleet ${selectedFleet.name}?`}
        onSubmit={handleDeleteFleet}
        onClose={() => setIsDeleteModalOpen(false)}
      />
      <EditFleetModal
        isOpen={isEditModalOpen}
        fleet={selectedFleet}
        allAircraft={allAircraft}
        onClose={() => setIsEditModalOpen(false)}
      />
      {isAddModalOpen && <AddFleetModal onClose={() => setIsAddModalOpen(false)} allAircraft={allAircraft} />}
    </>
  ) : <Typography>No fleets found</Typography>;
};
